import { forwardRef, useId } from 'react'
import { cn } from '@shared/lib/cn'
import { ErrorMessage } from '@shared/ui/ErrorMessage/ErrorMessage'
import { HintMessage } from '@shared/ui/HintMessage/HintMessage'
import { Label } from '@shared/ui/Label/Label'

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  hint?: string
  wrapperClassName?: string
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, error, hint, id, wrapperClassName, leftIcon, rightIcon, ...props }, ref) => {
    const generatedId = useId()
    const inputId = id ?? (label ? generatedId : undefined)
    return (
      <div className={cn('flex flex-col gap-1', wrapperClassName)}>
        {label && (
          <Label htmlFor={inputId} required={props.required}>
            {label}
          </Label>
        )}
        <div className="relative">
          {leftIcon && (
            <span className="text-placeholder pointer-events-none absolute top-1/2 left-3 flex -translate-y-1/2 items-center justify-center">
              {leftIcon}
            </span>
          )}
          <input
            ref={ref}
            id={inputId}
            className={cn(
              'border-border bg-surface h-10 w-full rounded-lg border px-3',
              'typo-lg text-foreground placeholder:text-placeholder',
              'focus:border-orange transition focus:outline-none',
              'disabled:cursor-not-allowed disabled:opacity-50',
              error && 'border-red focus:border-red',
              leftIcon && 'pl-9',
              rightIcon && 'pr-9',
              className
            )}
            {...props}
          />
          {rightIcon && (
            <span className="text-placeholder absolute top-1/2 right-3 flex -translate-y-1/2 items-center justify-center">
              {rightIcon}
            </span>
          )}
        </div>
        {error ? <ErrorMessage message={error} /> : <HintMessage message={hint} />}
      </div>
    )
  }
)
Input.displayName = 'Input'
